import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import { BLOG_ARTICLES } from "@/blog/articles";
import { BlogCard } from "@/components/BlogParts";
import { logger } from "@/lib/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const renderCard = (article) => <BlogCard key={article.slug} article={article} />;

export default function BlogPage() {
  const [dbArticles, setDbArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/blog/articles?limit=50`)
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        if (!cancelled) setDbArticles(items);
      })
      .catch((err) => logger.error("Blog fetch failed:", err));
    return () => { cancelled = true; };
  }, []);

  // Merge: DB articles first (newest), then static articles
  const allArticles = [...dbArticles, ...BLOG_ARTICLES];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="blog-index-page">
      <SEO
        title="Blog Mediastore Niort - Conseils Réparation iPhone, Mac & Tech"
        description="Conseils, guides et astuces sur la réparation Apple, iPhone, Mac, iPad et smartphones par les experts Mediastore à Niort."
        keywords="blog mediastore niort, conseils réparation iphone, guide mac, astuces apple niort, blog réparation téléphone"
        path="/blog"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Blog Mediastore Niort",
          "url": "https://www.mediastore79.com/blog",
          "publisher": {
            "@type": "Organization",
            "name": "Mediastore",
            "url": "https://www.mediastore79.com"
          }
        }}
      />

      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container-custom max-w-5xl section-padding text-center">
          <Badge className="bg-red-600 text-white mb-6 px-4 py-2 hover:bg-red-600 inline-flex items-center gap-2">
            <Newspaper className="h-4 w-4" />
            Blog Mediastore
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Conseils & guides réparation
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Tout savoir sur la réparation iPhone, Mac, iPad et smartphones. Les conseils de nos
            techniciens certifiés Apple à Niort.
          </p>
          {dbArticles.length > 0 && (
            <p className="text-sm text-slate-400 mt-4 inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-red-400" />
              {dbArticles.length} {dbArticles.length > 1 ? "nouveaux articles" : "nouvel article"} mis à jour récemment
            </p>
          )}
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {allArticles.map(renderCard)}
          </div>

          <div className="text-center mt-16">
            <p className="text-slate-600">
              Nouveaux articles publiés régulièrement — restez à l&apos;écoute !
            </p>
            <Link
              to="/contact"
              className="inline-block mt-4 text-red-600 hover:text-red-700 font-semibold"
              data-testid="blog-suggest-cta"
            >
              Une question particulière ? Contactez-nous →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
